const FALLBACK_TIME_ZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Calcutta",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

function isValidTimeZone(timeZone?: string | null) {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

export function getDefaultTimeZone() {
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return isValidTimeZone(browserTimeZone) ? browserTimeZone : "UTC";
}

function getValidTimeZone(timeZone?: string | null) {
  return isValidTimeZone(timeZone) ? String(timeZone) : getDefaultTimeZone();
}

export function getTimeZoneOptions(extraTimeZone?: string | null) {
  const supported =
    typeof (Intl as any).supportedValuesOf === "function"
      ? ((Intl as any).supportedValuesOf("timeZone") as string[])
      : FALLBACK_TIME_ZONES;
  return Array.from(new Set([getDefaultTimeZone(), "UTC", extraTimeZone || "", ...supported]))
    .filter(isValidTimeZone)
    .sort((a, b) => a.localeCompare(b));
}

function getZonedParts(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second || "0"),
  };
}

function pad(value: number) {
  return String(value).padStart(2, "0");
}

export function toDateTimeLocalInTimeZone(value?: string | null, timeZone?: string | null) {
  const date = value ? new Date(value) : new Date(Date.now() + 60 * 60 * 1000);
  if (Number.isNaN(date.getTime())) return "";
  const parts = getZonedParts(date, getValidTimeZone(timeZone));
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}

function parseDateTimeLocal(value: string) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
  };
}

function getTimeZoneOffsetMs(date: Date, timeZone: string) {
  const parts = getZonedParts(date, timeZone);
  const zonedAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return zonedAsUtc - date.getTime();
}

export function fromDateTimeLocalInTimeZone(value: string, timeZone?: string | null) {
  const parsed = parseDateTimeLocal(value);
  if (!parsed) throw new Error("Run time must be a valid datetime");
  const zone = getValidTimeZone(timeZone);
  const localAsUtc = Date.UTC(parsed.year, parsed.month - 1, parsed.day, parsed.hour, parsed.minute, 0);
  let utcTime = localAsUtc;
  for (let i = 0; i < 3; i += 1) {
    utcTime = localAsUtc - getTimeZoneOffsetMs(new Date(utcTime), zone);
  }
  return new Date(utcTime).toISOString();
}

export function formatDateTimeInTimeZone(value?: string | null, timeZone?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    timeZone: getValidTimeZone(timeZone),
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date);
}
